import { useEffect, useRef, useState } from "react";
import { generatePreview } from "@design-lab/features/manage-product-sizing/ui/generatePreview";
import type { SavedProject, CompositionPreviews } from "../types/savedProject.types";

export function useCompositionPreviews(compositions: SavedProject["compositions"]) {
  const [previews, setPreviews] = useState<Record<number, CompositionPreviews>>({});
  const [isGeneratingPreviews, setIsGeneratingPreviews] = useState(true);
  const previewsRef = useRef<Record<number, CompositionPreviews>>({});

  useEffect(() => {
    let isMounted = true;

    const generateAllPreviews = async () => {
      if (!isMounted) return;

      setIsGeneratingPreviews(true);
      const newPreviews: Record<number, CompositionPreviews> = {};

      try {
        await Promise.all(
          compositions.map(async (composition, index) => {
            if (!isMounted) return;

            newPreviews[index] = {};

            if (composition.front) {
              try {
                const preview = await generatePreview({
                  imageUrl: composition.front.imageUrl,
                  artworkSize: {
                    width: composition.front.width,
                    height: composition.front.height
                  },
                  surface: composition.front.surface
                });
                if (isMounted) {
                  newPreviews[index].front = URL.createObjectURL(preview.blob);
                }
              } catch {
                if (isMounted) {
                  newPreviews[index].front = composition.front.imageUrl;
                }
              }
            }

            if (composition.back) {
              try {
                const preview = await generatePreview({
                  imageUrl: composition.back.imageUrl,
                  artworkSize: {
                    width: composition.back.width,
                    height: composition.back.height
                  },
                  surface: composition.back.surface
                });
                if (isMounted) {
                  newPreviews[index].back = URL.createObjectURL(preview.blob);
                }
              } catch {
                if (isMounted) {
                  newPreviews[index].back = composition.back.imageUrl;
                }
              }
            }
          })
        );

        if (isMounted) {
          setPreviews(newPreviews);
          previewsRef.current = newPreviews;
        }
      } finally {
        if (isMounted) {
          setIsGeneratingPreviews(false);
        }
      }
    };

    generateAllPreviews();

    return () => {
      isMounted = false;
      Object.keys(previewsRef.current).forEach(key => {
        const compositionPreviews = previewsRef.current[Number(key)];
        if (compositionPreviews?.front?.startsWith("blob:")) {
          URL.revokeObjectURL(compositionPreviews.front);
        }
        if (compositionPreviews?.back?.startsWith("blob:")) {
          URL.revokeObjectURL(compositionPreviews.back);
        }
      });
    };
  }, [compositions]);

  return { previews, isGeneratingPreviews };
}
